import type { ReactNode } from "react";
import type { LogLineView } from "../api/types";
import { clock } from "../lib/format";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Empty as EmptyBox,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
} from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "cn";

/**
 * The apparatus a component library has no opinion about.
 *
 * Everything shadcn draws is imported from `@/components/ui` at the point it is used, so a button
 * here is a Button and a card is a Card. What is left in this file is the six things a console needs
 * that no registry ships: a lane dot, the label that marks a section, a counted figure, a ruled row,
 * a bot's own output and a diff.
 */

/** What a run's state is drawn in: the dot's own colour, and the ring around it. */
export function dotClass(status: string): string {
  switch (status) {
    case "RUNNING":
      return "bg-running ring-running/20";
    case "QUEUED":
      return "bg-queued ring-queued/20";
    case "WAITING_APPROVAL":
      return "bg-waiting ring-waiting/20";
    case "FAILED":
      return "bg-destructive ring-destructive/20";
    case "DONE":
      return "bg-muted-foreground ring-muted-foreground/20";
    default:
      return "bg-muted-foreground ring-muted-foreground/20";
  }
}

/** What a state is called in a lane header, which is not always what the enum is called. */
export function wordFor(status: string): string {
  if (status === "WAITING_APPROVAL") return "Waiting on you";
  return status.charAt(0) + status.slice(1).toLowerCase();
}

/** A lane's state, as a dot with a halo. The ring is a ring, not depth. */
export function LaneDot({
  status,
  held = false,
}: {
  status: string;
  held?: boolean;
}) {
  return (
    <span
      className={cn(
        "inline-block size-2 flex-none rounded-full ring-3",
        held ? "bg-background ring-input" : dotClass(status),
      )}
    />
  );
}

/** A finished run's state, as a word on a neutral ground. */
export function StatusPill({ status }: { status: string }) {
  return (
    <Badge variant="secondary" className="gap-1.5 font-normal">
      <LaneDot status={status} />
      {status.toLowerCase().replace(/_/g, " ")}
    </Badge>
  );
}

/** The uppercase label that marks a section, with the hairline under it. */
export function Apparatus({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="apparatus">{children}</div>
      <Separator />
    </div>
  );
}

/** An outlined capability or metadata label. */
export function Tag({ children }: { children: ReactNode }) {
  return (
    <Badge
      variant="outline"
      className="apparatus h-auto max-w-full shrink overflow-visible px-2 py-1 text-left whitespace-normal"
    >
      {children}
    </Badge>
  );
}

/**
 * A sentence where a screen would otherwise be blank, saying what would fill it.
 *
 * Not a rename of shadcn's Empty: that one is sans, muted and built around an icon. This is the
 * design's own answer — one serif line, centred, with an optional action under it.
 */
export function Empty({
  children,
  action,
}: {
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <EmptyBox className="border-0 bg-transparent">
      <EmptyHeader>
        <EmptyDescription className="font-serif text-heading text-foreground">
          {children}
        </EmptyDescription>
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </EmptyBox>
  );
}

/** One number, monospaced so it holds its width while it changes, with what it counts beneath. */
export function Figure({
  value,
  label,
  tone,
}: {
  value: string;
  label: string;
  tone?: "running" | "waiting" | "failed" | "muted";
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          "font-mono text-figure -tracking-[0.02em] tabular-nums",
          tone === "running"
            ? "text-running"
            : tone === "waiting"
              ? "text-waiting"
              : tone === "failed"
                ? "text-destructive"
                : tone === "muted"
                  ? "text-muted-foreground"
                  : undefined,
        )}
      >
        {value}
      </div>
      <div className="apparatus">{label}</div>
    </div>
  );
}

/**
 * A banner that needs an answer, or one that reports a failure.
 *
 * Carbon's inline notification: the ground is the layer colour and the status is carried by a bar
 * down the left edge. The role is passed through rather than left to shadcn, which hardcodes
 * `alert` — the approval banner re-renders every two seconds, and an assertive live region that
 * re-announces that often makes the dashboard unusable with a screen reader.
 */
export function Banner({
  title,
  children,
  tone,
  action,
}: {
  title: string;
  children: ReactNode;
  tone: "waiting" | "failed";
  action?: ReactNode;
}) {
  return (
    <Alert
      role={tone === "failed" ? "alert" : "status"}
      className={cn(
        "flex items-start justify-between gap-4 border-0 border-l-2 bg-card text-foreground",
        tone === "failed" ? "border-l-destructive" : "border-l-waiting",
      )}
    >
      <div className="grid gap-1">
        <AlertTitle
          className={tone === "failed" ? "text-destructive" : "text-waiting"}
        >
          {title}
        </AlertTitle>
        <AlertDescription className="text-foreground">
          {children}
        </AlertDescription>
      </div>
      {action}
    </Alert>
  );
}

/** A ruled row: a label, and a value in mono on the right. */
export function RuledRow({
  label,
  value,
  tone,
  last = false,
}: {
  label: string;
  value: string;
  tone?: string;
  last?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 border-b py-2.25",
        last && "border-b-0",
      )}
    >
      <span className="text-row text-muted-foreground">{label}</span>
      <span className={cn("ml-auto font-mono text-mono tabular-nums", tone)}>
        {value}
      </span>
    </div>
  );
}

/**
 * How a log line's level is coloured.
 *
 * A lookup rather than an interpolated class name: Tailwind reads the source to decide what to
 * generate, and a class built from a runtime string is one it cannot see.
 */
const LEVEL: Record<string, string> = {
  SAVED: "text-running",
  ERROR: "text-destructive",
  SKIPPED: "text-muted-foreground",
};

/** A run's own output, with the time faint and the verb coloured. */
export function LogLines({ lines }: { lines: LogLineView[] }) {
  return (
    <ScrollArea className="border bg-background" viewportClassName="max-h-65">
      <div className="flex flex-col gap-[3px] p-2">
        {lines.map((line) => (
          <div className="flex gap-2 font-mono text-mono" key={line.seq}>
            <span className="flex-none text-muted-foreground tabular-nums">
              {clock(line.at)}
            </span>
            {line.level && line.level !== "INFO" ? (
              <span className={cn("flex-none", LEVEL[line.level])}>
                {line.level.toLowerCase()}
              </span>
            ) : null}
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {line.message}
            </span>
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}

/** What a line of a unified diff is drawn in. */
function diffClass(line: string): string | undefined {
  if (line.startsWith("+")) return "bg-running/10 text-running";
  if (line.startsWith("-")) return "text-destructive";
  if (line.startsWith("@")) return "text-queued";
  return undefined;
}

/** A unified diff, coloured the way a diff is. */
export function Diff({ diff }: { diff: string }) {
  return (
    <ScrollArea className="border bg-background" viewportClassName="max-h-70">
      <div className="py-2 font-mono text-mono">
        {diff.split("\n").map((line, index) => (
          <div
            className={cn(
              "px-3.5 [overflow-wrap:anywhere] whitespace-pre-wrap text-muted-foreground",
              diffClass(line),
            )}
            key={index}
          >
            {line === "" ? " " : line}
          </div>
        ))}
      </div>
    </ScrollArea>
  );
}
